'use client';
import { FunctionComponent } from 'react';
import { t } from '@xFrame4/common/Utils';
import ALink from '@xFrame4/components/next/ALink';
import Company from '@business/general/Company';
import ContactInfoItem from './ContactInfoItem';
import AppTracker from '@components/tracking/AppTracker';

export interface ContactInfoProps
{
    company: Company;
    language: string;
}

/**
 * Component: displays company contact information including address, phone, email and social media links.
 */
const ContactInfo: FunctionComponent<ContactInfoProps> = (props) =>
{
    const company = Company.createFromJson(props.company) as Company;
    const translation = company.getTranslation(props.language);

    /** Render address content */
    const cmpAddressContent = (translation?.addressLine1 || translation?.cityName) && (
        <>
            {translation?.addressLine1 && (
                <div>
                    <ALink
                        href={`https://maps.app.goo.gl/ZdUZhuF2otVFGePe9`}
                        target="_blank"
                        className="text-decoration-none text-primary"
                        onClick={() => AppTracker.trackAddressClick()}
                    >
                        {translation.addressLine1}
                    </ALink>
                </div>
            )}
            {translation?.addressLine2 && <div>{translation.addressLine2}</div>}
            <div>
                {translation?.cityName && translation.cityName}
                {translation?.cityName && company.postalCode && ', '}
                {company.postalCode && company.postalCode}
            </div>
            {translation?.regionName && <div>{translation.regionName}</div>}
            {translation?.countryName && <div>{translation.countryName}</div>}
        </>
    );

    /** Render phone content */
    const cmpPhoneContent = company.phone && (
        <a href={`tel:${company.phone}`} className="text-decoration-none" onClick={() => AppTracker.trackPhoneClick()}>
            {company.phone}
        </a>
    );

    /** Render email content */
    const cmpEmailContent = company.email && (
        <a href={`mailto:${company.email}`} className="text-decoration-none" onClick={() => AppTracker.trackEmailClick()}>
            {company.email}
        </a>
    );

    /** Render */
    return (
        <div className="contact-info">
            <div className="contact-info-header mb-4 text-uppercase text-primary">
                <h4 className="font-size-6">{t('CONTACT', props.language)}</h4>
            </div>

            {cmpAddressContent && (
                <ContactInfoItem
                    icon="fas fa-location-dot"
                    title={t('ADDRESS', props.language)}
                    valueContent={cmpAddressContent}
                    className="mb-3"
                />
            )}

            {cmpPhoneContent && (
                <ContactInfoItem
                    icon="fas fa-phone"
                    title={t('PHONE', props.language)}
                    valueContent={cmpPhoneContent}
                    className="mb-3"
                />
            )}

            {cmpEmailContent && (
                <ContactInfoItem
                    icon="fas fa-envelope"
                    title={t('EMAIL', props.language)}
                    valueContent={cmpEmailContent}
                    className="mb-3"
                />
            )}
        </div>
    );
}

export default ContactInfo; 
